import React, { FC, PropsWithChildren, useRef, useState } from 'react';
import styled from '@emotion/styled';
import { observer } from 'mobx-react';
import Sidebar from '@app/components/sidebar/Sidebar';
import Footer from '@app/components/Footer';
import MorteeMode from '@mortee/morteeMode';
import useScrollTopOnLocationChange from '@app/hooks/useScrollTopOnLocationChange';
import useInfraStores from '@app/hooks/useInfraStores';
import AuthenticationProvider from '@app/login/AuthenticationProvider';
import { useLocation } from 'react-router-dom';
import { IconButton } from '@mui/material';
import { Menu } from 'lucide-react';

const MorteeAppContent: FC<PropsWithChildren> = observer((props) => {
  const { userStore } = useInfraStores<MorteeMode>();
  const appContentContainerRef = useRef<HTMLDivElement | null>(null);
  const location = useLocation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  useScrollTopOnLocationChange(appContentContainerRef);

  const isValidationSystemRoute = location.pathname.includes('/admin/validation-system');

  const handleDrawerToggle = (): void => {
    setDrawerOpen(!drawerOpen);
  };

  return (
    <AuthenticationProvider>
      <PageLayout>
        {!isValidationSystemRoute && <StyledSidebar />}
        {isValidationSystemRoute && drawerOpen && <DrawerSidebar />}
        <ContentLayout
          id='appContentContainer'
          data-testid='app-content-container'
          ref={appContentContainerRef}
          $isValidationSystemRoute={isValidationSystemRoute}
        >
          {isValidationSystemRoute && (
            <MenuButtonContainer $drawerOpen={drawerOpen}>
              <IconButton onClick={handleDrawerToggle} sx={{ color: 'var(--app-main-color)' }} aria-label='open drawer'>
                <Menu size={24} />
              </IconButton>
            </MenuButtonContainer>
          )}
          <ContentContainer id='main' key={userStore.selectedOrganization?.id}>
            {props.children}
          </ContentContainer>
          <StyledFooter />
        </ContentLayout>
      </PageLayout>
    </AuthenticationProvider>
  );
});

export default MorteeAppContent;

const ContentLayout = styled.main<{ $isValidationSystemRoute: boolean }>`
  overflow: auto;
  overflow-y: scroll;
  padding-bottom: 32px;

  // Borrowed from antd's Layout
  display: flex;
  flex: auto;
  flex-direction: column;
  min-height: 0;

  ${(props): string =>
    props.$isValidationSystemRoute
      ? 'font-family: "SF Pro", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;'
      : ''}
`;

const ContentContainer = styled.div`
  padding-bottom: 32px;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
  align-items: stretch;
`;

const StyledSidebar = styled(Sidebar)`
  flex: 0 0 auto;
  width: 220px;
`;

const DrawerSidebar = styled(Sidebar)`
  flex: 0 0 auto;
  width: 220px;
  transition: transform 0.3s ease-in-out;
`;

const StyledFooter = styled(Footer)`
  flex: 0 0 auto;
  text-align: center;
`;

const PageLayout = styled.div`
  display: flex;
  flex: auto;
  flex-direction: row;

  height: 100vh;
  min-height: 0;
`;

const MenuButtonContainer = styled.div<{ $drawerOpen: boolean }>`
  position: fixed;
  top: 16px;
  left: ${(props): string => (props.$drawerOpen ? '220px' : '16px')};
  z-index: 16;
  transition: left 0.3s ease-in-out;
`;
